import { ApplicationRef, ComponentFactory, ComponentFactoryResolver, ComponentRef, Injectable, Injector, ReflectiveInjector, TemplateRef, ViewRef } from '@angular/core';
import { ModalComponent } from './modal.component';

@Injectable()
export class ModalService {
  private modalFactory: ComponentFactory<ModalComponent>;

  constructor(
    private appRef: ApplicationRef,
    private cfr: ComponentFactoryResolver,
    private injector: Injector
  ) {
    this.modalFactory = cfr.resolveComponentFactory(ModalComponent);
  }

  open(content: any, options: any = {}): ModalRef {
    const containerSelector = options.container || 'body';
    const containerEl = document.querySelector(containerSelector);

    if (!containerEl) {
      throw new Error(`Modal container '${containerSelector}' was not found in the DOM.`);
    }

    const activeModal = new ActiveModal();
    const modalContent = this.getContent(content, activeModal);
    const modalComponent = this.modalFactory.create(this.injector, modalContent.nodes);
    this.applyModalOptions(modalComponent.instance, options);
    this.appRef.attachView(modalComponent.hostView);
    containerEl.appendChild(modalComponent.location.nativeElement);

    const modalRef = new ModalRef(modalComponent, modalContent);

    activeModal.close = (result) => modalRef.close(result);
    activeModal.dismiss = () => modalRef.dismiss();

    return modalRef;
  }

  private getContent(content: any, context: ActiveModal): ContentRef {
    if (content instanceof TemplateRef) {
      const view = content.createEmbeddedView(context);
      this.appRef.attachView(view);
      return new ContentRef([view.rootNodes], view);
    } else {
      const contentFactory = this.cfr.resolveComponentFactory(content);
      const contentInjector = ReflectiveInjector.resolveAndCreate([{ provide: ActiveModal, useValue: context }], this.injector);
      const contentComponent = contentFactory.create(contentInjector);
      this.appRef.attachView(contentComponent.hostView);
      return new ContentRef([[contentComponent.location.nativeElement]], contentComponent.hostView, contentComponent);
    }
  }

  private applyModalOptions(modalInstance: ModalComponent, options: Object) {
    ['backdrop', 'keyboard', 'size', 'windowClass'].forEach((optionName: string) => {
      let value = options[optionName];
      if (value !== undefined && value !== null) {
        modalInstance[optionName] = options[optionName];
      }
    });
  }
}

export class ModalRef {
  result: Promise<any>;

  private resolve: (result) => void;
  private reject: () => void;

  constructor(
    private modalComponent: ComponentRef<ModalComponent>,
    private modalContent: ContentRef
  ) {
    modalComponent.instance.dismiss.subscribe(() => this.dismiss());

    this.result = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
    this.result.catch(() => {});
  }

  get componentInstance(): any {
    if (this.modalContent.componentRef) {
      return this.modalContent.componentRef.instance;
    }
  }

  close(result): void {
    if (this.modalComponent) {
      this.resolve(result);
      this.destroy();
    }
  }

  dismiss(): void {
    if (this.modalComponent) {
      this.reject();
      this.destroy();
    }
  }

  private destroy() {
    const modalEl = this.modalComponent.location.nativeElement;
    modalEl.parentNode.removeChild(modalEl);
    this.modalComponent.destroy();

    if (this.modalContent && this.modalContent.viewRef) {
      this.modalContent.viewRef.destroy();
    }

    this.modalComponent = null;
    this.modalContent = null;
  }
}

export class ContentRef {
  constructor(
    public nodes: any[],
    public viewRef: ViewRef,
    public componentRef?: ComponentRef<any>
  ) {}
}

export class ActiveModal {
  close(result) {}
  dismiss() {}
}



// WEBPACK FOOTER //
// ./src/app/shared/modal/modal.service.ts